import { defineComponent as _defineComponent } from 'vue'
import { normalizeStyle as _normalizeStyle, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createVNode as _createVNode, withCtx as _withCtx, createElementBlock as _createElementBlock, createTextVNode as _createTextVNode, normalizeClass as _normalizeClass, renderList as _renderList, Fragment as _Fragment } from "vue"

const _hoisted_1 = { class: "create-edit-contact" }
const _hoisted_2 = {
  key: 1,
  class: "create-edit-contact__header"
}
const _hoisted_3 = { class: "create-edit-contact__header-title" }
const _hoisted_4 = { class: "create-edit-contact__body" }
const _hoisted_5 = ["data-invalid-levels"]
const _hoisted_6 = { class: "create-edit-contact__body-tags" }
const _hoisted_7 = { class: "create-edit-contact__footer" }

import {
	computed,
	nextTick,
	onMounted,
	ref,
	watch
} from "vue";
import {
	clientsStore,
	Client,
	NewContact,
	Tag,
	AddressChainLevel
} from "@/store/clients";
import {PersonApi} from "@/ api/PersonApi";
import { auxStore } from "@/store/auxillary";
import AppInput from "@/components/ui-kit/input/AppInput.vue";
import AppTextarea from "@/components/ui-kit/textarea/AppTextarea.vue";
import AppIcon from "@/components/ui-kit/icon/AppIcon.vue";
import AppLoader from "@/components/common/AppLoader.vue";
import AppSelect from "@/components/ui-kit/select/AppSelect.vue";
import AppButton from "@/components/ui-kit/button/AppButton.vue";
import AppResizableDropdown from "@/components/ui-kit/dropdown/AppResizableDropdown.vue";
import AppTagSelector from "@/components/ui-kit/tag-selector/AppTagSelector.vue";
import AppTooltip from "@/components/ui-kit/tooltip/AppTooltip.vue";
import InlineAddressEditor from "@/components/common/address-selector/InlineAddressEditor.vue";
import { TagEntityType, TagBaseType } from "@/models/etc/types";
import { TagWithFrequency } from "@/models/interfaces/tags/tag-models";
import { useContactsContext } from "@/composables/useContactsContext";
import { useBreakpoints } from "@/composables/useBreakpoints";
import { AddressInputComponentOut } from "@/models/interfaces/entity-settings";
import {
	missingRequiredAddressLevels,
	RequestAddressContainer,
	toFullRequestContainer
} from "@/composables/useAddressRequest";

type CreateEditContactProps = {
	client?: Client,
	type?: 'create' | 'edit'
}


export default /*@__PURE__*/_defineComponent({
  __name: 'CreateEditContactDropdown',
  props: {
    client: {},
    type: { default: 'create' }
  },
  emits: ["on-close", "exist-contact-string"],
  setup(__props: any, { emit: __emit }) {

const emit = __emit

const props = __props

const { isMobile } = useBreakpoints()

const $nameRef = ref(null)
const $postRef = ref(null)
const $addTag = ref<typeof AppResizableDropdown | null>(null)
const store = clientsStore()
const ctx = useContactsContext()
const selectedAttachClientId = ref<number | null>(null)
const attachClientItems = computed(() =>
	(ctx.attachClientOptions ?? []).map(c => ({ id: c.Id, label: c.Name }))
)
const isInit = ref(false)
const isLoading = ref(false)
const isContactExist = ref(false)
const isContactExistArray = ref<Client[]>([])

const nameError = ref(false)
const postError = ref(false)
const contactsError = ref(false)

const invalidAddressLevels = ref<number[]>([])
const invalidAddressLevelsAttr = computed(() => invalidAddressLevels.value.join(' '))

const filters = ref<Client | NewContact>({
	Name: '',
	Post: '',
	Contacts: '',
	InputContainer: {
		components: [],
		activeLevel: 0
	},
	Tags: []
})

const contactAddressChain = ref<RequestAddressContainer | null>(null)
const $addressEditor = ref<{ flush: () => void } | null>(null)

const addressStructure = (): AddressInputComponentOut[] =>
	(auxStore().getCachedAddressContainer ?? []) as AddressInputComponentOut[]

// Сервер ждёт в InputContainer все уровни структуры: незаполненные — с value: "".
// В ответе пустых уровней нет, поэтому добираем их из структуры перед отправкой.
const fullAddressContainer = (): RequestAddressContainer =>
	toFullRequestContainer(contactAddressChain.value, addressStructure())

const addressChainToContainer = (chain: AddressChainLevel[]): RequestAddressContainer | null => {
	const structure = addressStructure()
	const nodeByLevel = new Map(structure.map(s => [s.Level, s.AddressNodeId]))
	const components = chain
		.filter(l => (l.Name ?? "").trim().length > 0)
		.map(l => ({ addressNodeId: nodeByLevel.get(l.Level) ?? 0, level: l.Level, value: l.Name }))
	if (!components.length) {
		return null
	}

	return {
		components,
		activeLevel: Math.max(0, ...components.map(c => c.level))
	}
}

const isNameFilled = computed(() => !!filters.value.Name?.trim())
const isPostFilled = computed(() => !!filters.value.Post?.trim())
const isContactsFilled = computed(() => !!filters.value.Contacts?.trim())
const missingAddressLevels = computed(
	() => missingRequiredAddressLevels(contactAddressChain.value, addressStructure())
)

const isFormValid = computed(() =>
	isNameFilled.value
	&& isPostFilled.value
	&& isContactsFilled.value
	&& !missingAddressLevels.value.length
)

const contactTagsModel = computed<TagWithFrequency[]>(
	() => (filters.value.Tags ?? []) as unknown as TagWithFrequency[]
)

const onSelectorChange = (nextTags: TagWithFrequency[]) => {
	filters.value.Tags = nextTags as unknown as Tag[]
}

const onCreateTag = (name: string) => {
	const normalized = name.trim().toLowerCase()
	if (!normalized) {
		return
	}
	const current = (filters.value.Tags ?? []) as Tag[]
	if (current.some(tag => tag.Name.toLowerCase().trim() === normalized)) {
		return
	}
	const tempId = Math.min(0, ...current.map(tag => tag.Id)) - 1
	filters.value.Tags = [...current, { Id: tempId, Name: normalized, Type: TagBaseType.Common }]
}

const onRemoveTag = (tagId: number) => {
	filters.value.Tags = (filters.value.Tags as Tag[]).filter(t => t.Id !== tagId)
}

const onSave = async () => {
	try {
		isLoading.value = true
		$addressEditor.value?.flush()
		if (!checkErrors()) {
			if (props.type === 'create') {
				localStorage.removeItem('newContact')
				const newContact = await store.createContact(buildCreatePayload())
				if (ctx.context === 'project' && ctx.projectId != null && newContact?.Id != null) {
					await PersonApi.setMainInProjectStatus(ctx.projectId, newContact.Id)
				}
				if (ctx.refetchContacts) {
					await ctx.refetchContacts()
				}
			} else {
				await store.updateContact(props.client!, mappedValuesForSave())
			}
			emit('on-close')
		}
	} catch (e) {
		console.error(e)
	} finally {
		isLoading.value = false
	}
}

const buildCreatePayload = (): NewContact => {
	const payload = { ...(filters.value as NewContact) }
	payload.Tags = (filters.value.Tags as Tag[]).map(tag => tag.Name)
	payload.InputContainer = fullAddressContainer()
	if (ctx.context === 'client' && ctx.attachClientId != null) {
		payload.ClientId = ctx.attachClientId
	} else if (ctx.context === 'project' && selectedAttachClientId.value != null) {
		payload.ClientId = selectedAttachClientId.value
	} else {
		payload.ClientId = 0
	}
	return payload
}

const onOpen = async () => {
	try {
		isLoading.value = true
		// Структура адреса нужна обоим режимам: на редактировании — чтобы поднять
		// AddressNodeId по уровням, на создании — чтобы добрать пустые уровни в payload.
		await auxStore().getAddressContainerStructure()
		if (props.type === 'create') {
			const newContactFromLS = localStorage.getItem('newContact');
			filters.value = newContactFromLS
				? JSON.parse(newContactFromLS)
				: {
					Name: '',
					Post: '',
					Contacts: '',
					InputContainer: {
						components: [],
						activeLevel: 0
					},
					Tags: []
				};
			contactAddressChain.value = null
		} else {
			filters.value = structuredClone(props.client)
			contactAddressChain.value = addressChainToContainer(props.client?.AddressChain ?? [])
		}
		setTimeout(() => {
			isInit.value = true
		}, 100)
		await nextTick(() => props.type === 'create'
			? $nameRef.value?.focusInput()
			: undefined
		)
	} catch (e) {
		console.error(e)
	} finally {
		isLoading.value = false
	}
}

const checkErrors = () => {
	nameError.value = !isNameFilled.value
	postError.value = !isPostFilled.value
	contactsError.value = !isContactsFilled.value
	invalidAddressLevels.value = missingAddressLevels.value

	return nameError.value || postError.value || contactsError.value || invalidAddressLevels.value.length > 0
}

const mappedValuesForSave = () => {
	return {
		PersonId: (filters.value as Client).Id!,
		Name: filters.value.Name,
		Post: filters.value.Post,
		Contacts: filters.value.Contacts,
		InputContainer: fullAddressContainer(),
		Tags: (filters.value.Tags as Tag[]).map(tag => tag.Name)
	}
}

const showExistContacts = async () => {
	if (isContactExistArray.value.length) {
		await ctx.showExistContacts?.(
			isContactExistArray.value.map(contact => contact.Id),
			filters.value.Contacts
		)

		if (props.type === 'edit') {
			emit('on-close')
		}

		emit('exist-contact-string', filters.value.Contacts)
	}
}

watch(() => filters.value.Contacts, async (newVal) => {
	if (newVal?.length >= 8 && isInit.value) {
		isContactExistArray.value = await PersonApi.checkContactIsExist(newVal.split(' ').join(''))
		isContactExist.value = !!isContactExistArray.value.length;
	} else {
		isContactExist.value = false
		isContactExistArray.value = []
	}
})

watch(contactAddressChain, () => {
	// До первой попытки сохранения молчим. После — пересчитываем, чтобы подсветка
	// снималась с уровней по мере заполнения. Безусловный сброс тут не годится:
	// watcher'ы flush: 'pre' асинхронны, и вызов после flush() в onSave затирал бы
	// подсветку, выставленную checkErrors(), ещё до отрисовки.
	if (!invalidAddressLevels.value.length) {
		return
	}
	invalidAddressLevels.value = missingAddressLevels.value
})

watch(filters, () => {
	if (props.type === 'create') {
		localStorage.setItem('newContact', JSON.stringify(filters.value))
	}
}, {
	deep: true,
})

watch(attachClientItems, (items) => {
	if (props.type === 'create' && ctx.context === 'project' && selectedAttachClientId.value == null) {
		selectedAttachClientId.value = (items[0]?.id as number) ?? null
	}
}, { immediate: true })

onMounted(() => onOpen())

const loaderStyle = computed(() => ({
	'background-color': 'rgba(255, 255, 255, 0.5)'
}))

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    (isLoading.value)
      ? (_openBlock(), _createBlock(AppLoader, {
          key: 0,
          style: _normalizeStyle(loaderStyle.value)
        }, null, 8, ["style"]))
      : _createCommentVNode("", true),
    (!_unref(isMobile))
      ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
          _createElementVNode("div", _hoisted_3, _toDisplayString(_ctx.$t(props.type === 'create' ? 'contact_create_title' : 'contact_edit_title')), 1),
          _createVNode(AppTooltip, {
            text: _ctx.$t('close_label')
          }, {
            activator: _withCtx(() => [
              _createVNode(AppIcon, {
                class: "create-edit-contact__header-close",
                name: "close",
                width: "20px",
                color: "#979797",
                "hover-color": "#499259",
                cursor: "pointer",
                onClick: _cache[0] || (_cache[0] = ($event: any) => (emit('on-close')))
              })
            ]),
            _: 1
          }, 8, ["text"])
        ]))
      : _createCommentVNode("", true),
    _createElementVNode("div", _hoisted_4, [
      (props.type === 'create' && _unref(ctx).context === 'project')
        ? (_openBlock(), _createBlock(AppSelect, {
            key: 0,
            modelValue: selectedAttachClientId.value,
            "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((selectedAttachClientId).value = $event)),
            items: attachClientItems.value,
            class: "create-edit-contact__search",
            placeholder: _ctx.$t('contact_project_client')
          }, null, 8, ["modelValue", "items", "placeholder"]))
        : _createCommentVNode("", true),
      _createVNode(AppInput, {
        ref_key: "$nameRef",
        ref: $nameRef,
        modelValue: filters.value.Name,
        "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((filters.value.Name) = $event)),
        class: "create-edit-contact__search",
        placeholder: _ctx.$t('name_short'),
        error: nameError.value,
        onClearError: _cache[3] || (_cache[3] = ($event: any) => (nameError.value = false))
      }, null, 8, ["modelValue", "placeholder", "error"]),
      _createVNode(AppInput, {
        ref_key: "$postRef",
        ref: $postRef,
        modelValue: filters.value.Post,
        "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((filters.value.Post) = $event)),
        class: "create-edit-contact__search",
        placeholder: _ctx.$t('position'),
        error: postError.value,
        onClearError: _cache[5] || (_cache[5] = ($event: any) => (postError.value = false))
      }, null, 8, ["modelValue", "placeholder", "error"]),
      _createVNode(AppTextarea, {
        class: "create-edit-contact__textarea",
        modelValue: filters.value.Contacts,
        "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((filters.value.Contacts) = $event)),
        height: "100%",
        placeholder: _ctx.$t('contact_info'),
        error: contactsError.value,
        onInput: _cache[7] || (_cache[7] = ($event: any) => (contactsError.value = false))
      }, null, 8, ["modelValue", "placeholder", "error"]),
      _createElementVNode("div", {
        class: _normalizeClass({
						'create-edit-contact__contact-exist': true,
						'create-edit-contact__contact-exist--show': isContactExist.value
					}),
        onClick: showExistContacts
      }, [
        _createVNode(AppIcon, {
          name: "user-exist",
          size: "16px"
        }),
        _createTextVNode(" " + _toDisplayString(_ctx.$t('contact_maybe_exists')), 1)
      ], 2),
      _createElementVNode("div", {
        class: _normalizeClass({
					'create-edit-contact__address': true,
					'create-edit-contact__address--error': invalidAddressLevels.value.length > 0
				}),
        "data-invalid-levels": invalidAddressLevelsAttr.value
      }, [
        _createVNode(InlineAddressEditor, {
          ref_key: "$addressEditor",
          ref: $addressEditor,
          id: "create-edit-contact",
          modelValue: contactAddressChain.value,
          "onUpdate:modelValue": _cache[8] || (_cache[8] = ($event: any) => ((contactAddressChain).value = $event)),
          "menu-z-index": 10010,
          "always-editing": true
        }, null, 8, ["modelValue"])
      ], 10, _hoisted_5),
      _createElementVNode("div", _hoisted_6, [
        _createVNode(AppResizableDropdown, {
          ref_key: "$addTag",
          ref: $addTag,
          position: "left",
          "close-by-click-outside": true,
          "max-width": 640,
          "max-height": 460,
          "mobile-title": _ctx.$t('choose_tags'),
          name: "$addTagCreateEditContact"
        }, {
          activator: _withCtx(() => [
            _createVNode(AppTooltip, {
              text: _ctx.$t('file_add_tag')
            }, {
              activator: _withCtx(() => [
                _createVNode(AppIcon, {
                  class: "create-edit-contact__body-tags-add",
                  name: "hash",
                  width: "16px",
                  color: "#979797",
                  "hover-color": "#499259",
                  cursor: "pointer"
                })
              ]),
              _: 1
            }, 8, ["text"])
          ]),
          default: _withCtx(() => [
            _createVNode(AppTagSelector, {
              "model-value": contactTagsModel.value,
              "entity-type": _unref(TagEntityType).Person,
              mode: "select",
              "allow-create": "",
              "onUpdate:modelValue": onSelectorChange,
              onCreateTag: onCreateTag,
              onClose: _cache[9] || (_cache[9] = ($event: any) => ($addTag.value?.close()))
            }, null, 8, ["model-value", "entity-type"])
          ]),
          _: 1
        }, 8, ["mobile-title"]),
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(filters.value.Tags, (tag) => {
          return (_openBlock(), _createElementBlock("span", {
            class: "create-edit-contact__body-tags-item",
            key: tag.Id
          }, [
            _createTextVNode(_toDisplayString(tag.Name.toLowerCase().trim()) + " ", 1),
            _createVNode(AppTooltip, {
              text: _ctx.$t('ui_tag_detach')
            }, {
              activator: _withCtx(() => [
                _createVNode(AppIcon, {
                  class: "create-edit-contact__body-tags-item-delete",
                  name: "close",
                  width: "12px",
                  color: "#D2D2D2",
                  "hover-color": "#499259",
                  cursor: "pointer",
                  onClick: ($event: any) => (onRemoveTag(tag.Id))
                }, null, 8, ["onClick"])
              ]),
              _: 2
            }, 1032, ["text"])
          ]))
        }), 128))
      ])
    ]),
    _createElementVNode("div", _hoisted_7, [
      _createVNode(AppButton, {
        label: _ctx.$t(props.type === 'create' ? 'create_short' : 'save'),
        onClick: onSave,
        disabled: isLoading.value || !isFormValid.value
      }, null, 8, ["label", "disabled"])
    ])
  ]))
}
}

})